import { getApiTable, hasApiTable, isApiSuccess, pickApiRowValue, getStaffTrainingAdditionalRolesFromResponse } from "./qmsApiUtils";
import { getVaultAuditLogEntries } from "./qmsVaultAuditLogUtils";
import { getVaultDocumentListId, getVaultDocumentTitle } from "./qmsVaultUtils";

export function getVaultDocumentInfo(response) {
  return getApiTable(response, "DocumentInfo")[0] || {};
}

export function getVaultCheckoutRecords(response) {
  return getApiTable(response, "CheckOutRecords");
}

export function getVaultReviewerPicker(response) {
  return getApiTable(response, "ReviewerList");
}

export function getVaultApproverPicker(response) {
  return getApiTable(response, "ApproverList");
}

export function getVaultSavedReviewers(response) {
  return dedupeVaultPeopleRows(getApiTable(response, "SavedReviewer"), "ReviewerId");
}

export function getVaultSavedApprovers(response) {
  return dedupeVaultPeopleRows(getApiTable(response, "SavedApprover"), "ApproverId");
}

function dedupeVaultPeopleRows(rows, idField) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    const id = String(row?.[idField] || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function getVaultPreviousVersions(response) {
  return getApiTable(response, "PreviousVersionDocuments");
}

export function getVaultAuditLog(response) {
  return getVaultAuditLogEntries(response);
}

export function getVaultPublishedDocuments(response) {
  return getApiTable(response, "PublishedDocuments");
}

export function getVaultSavedAssociatedDocuments(response) {
  return getApiTable(response, "SavedAssocaitedDocument");
}

export function getVaultSavedMyLearningUsers(response) {
  return getApiTable(response, "SavedMyLearningUsers");
}

export function getVaultDocumentLibraryUsers(response) {
  return getApiTable(response, "DocumentLibraryUsers");
}

export function getVaultDocumentLibraryStaffList(response) {
  return getApiTable(response, "DocumentLibraryStaffList");
}

export function getVaultStaffList(response) {
  return getApiTable(response, "StaffList");
}

export function getVaultStaffTrainingAdditionalRoles(response) {
  return getStaffTrainingAdditionalRolesFromResponse(response);
}

/** Apply My Learning / Document Library staff API tables without full reload. */
export function patchVaultWorkspaceStaffTables(workspaceResponse, actionResponse) {
  if (!isApiSuccess(actionResponse)) return workspaceResponse;

  const current =
    workspaceResponse && typeof workspaceResponse === "object" ? workspaceResponse : {};
  const data = current.data ?? current.Data ?? {};
  const nextData = { ...data };

  const myLearning = getApiTable(actionResponse, "SavedMyLearningUsers");
  if (hasApiTable(actionResponse, "SavedMyLearningUsers")) {
    nextData.SavedMyLearningUsers = myLearning;
  }

  const libraryUsers = getApiTable(actionResponse, "DocumentLibraryUsers");
  if (hasApiTable(actionResponse, "DocumentLibraryUsers")) {
    nextData.DocumentLibraryUsers = libraryUsers;
  }

  return {
    ...current,
    data: nextData,
    Data: nextData,
  };
}

/** Apply Save/Remove associated-documents API tables without reloading the full workspace. */
export function patchVaultWorkspaceAssociatedDocs(workspaceResponse, actionResponse) {
  if (!isApiSuccess(actionResponse)) return workspaceResponse;

  const current = workspaceResponse && typeof workspaceResponse === "object" ? workspaceResponse : {};
  const data = current.data ?? current.Data ?? {};
  const nextData = { ...data };
  nextData.SavedAssocaitedDocument = getApiTable(actionResponse, "SavedAssocaitedDocument");

  const documentLog = getApiTable(actionResponse, "DocumentLog");
  if (documentLog.length > 0) {
    nextData.DocumentLog = documentLog;
  }

  return {
    ...current,
    data: nextData,
    Data: nextData,
  };
}

/** Apply comment action DocumentLog without reloading the full workspace. */
export function patchVaultWorkspaceDocumentLog(workspaceResponse, actionResponse) {
  if (!isApiSuccess(actionResponse)) return workspaceResponse;

  const documentLog = getApiTable(actionResponse, "DocumentLog");
  if (!documentLog.length) return workspaceResponse;

  const current = workspaceResponse && typeof workspaceResponse === "object" ? workspaceResponse : {};
  const data = current.data ?? current.Data ?? {};
  const nextData = { ...data, DocumentLog: documentLog };

  return {
    ...current,
    data: nextData,
    Data: nextData,
  };
}

export const VAULT_COMMENT_PRIORITIES = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

export function normalizeVaultCommentPriority(priority) {
  const value = String(priority || "Medium").trim().toLowerCase();
  if (value === "critical" || value === "high") return "High";
  if (value === "low") return "Low";
  if (value === "standard" || value === "medium") return "Medium";
  return "Medium";
}

export function parseVaultTaggedStaffIds(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeStaffId(value) {
  return String(value || "").trim().toLowerCase();
}

export function isVaultCommentOwner(comment, currentStaffId) {
  if (!comment) return false;
  const ownerId = normalizeStaffId(comment.CreatedBy ?? comment.createdBy);
  const staffId = normalizeStaffId(currentStaffId);
  return Boolean(ownerId && staffId && ownerId === staffId);
}

export function getVaultCommentReplyCount(comment) {
  return Number(comment?.ReplyCount ?? comment?.replyCount ?? 0);
}

export function canModifyVaultComment(comment, currentStaffId) {
  if (!isVaultCommentOwner(comment, currentStaffId)) return false;
  return getVaultCommentReplyCount(comment) === 0;
}

export function resolveVaultCommentMentionIds(draft, staffOptions, existingIds = []) {
  const ids = new Set((existingIds || []).map((id) => String(id)));
  const text = String(draft || "");

  (staffOptions || []).forEach((staff) => {
    const label = String(
      staff?.UserName ||
        [staff?.FirstName, staff?.LastName].filter(Boolean).join(" ") ||
        staff?.StaffName ||
        "",
    ).trim();
    const id = String(staff?.StaffId || staff?.staffId || "").trim();
    if (!label || !id) return;

    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`@${escaped}(?:\\s|$|[.,!?;:])`, "i");
    if (pattern.test(text)) ids.add(id);
  });

  return [...ids];
}

export function getVaultCommentPriorityClass(priority) {
  const normalized = normalizeVaultCommentPriority(priority).toLowerCase();
  if (normalized === "high") return "is-high";
  if (normalized === "low") return "is-low";
  return "is-medium";
}

export function getVaultQuizQuestions(response) {
  return getApiTable(response, "QuizzQuestions");
}

export function getVaultQuizOptions(response) {
  return getApiTable(response, "QuizzQuestionOptions");
}

/** Staff who may be @mentioned on comments. */
function appendVaultMentionStaff(target, staffId, userName) {
  const id = String(staffId || "").trim();
  const label = String(userName || "").trim();
  if (!id || !label) return;
  target.push({ StaffId: id, UserName: label });
}

function dedupeVaultMentionStaff(rows) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    const id = String(row.StaffId || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return Boolean(String(row.UserName || "").trim());
  });
}

function getVaultDocumentOwnerMention(response) {
  const info = getVaultDocumentInfo(response);
  const ownerName = String(info.CreatedBy || "").trim();
  if (!ownerName) return null;

  const ownerId = pickApiRowValue(
    info,
    "CreatedByStaffId",
    "OwnerStaffId",
    "CreatedById",
    "OwnerId",
  );
  if (ownerId) {
    return { StaffId: ownerId, UserName: ownerName };
  }

  const staffMatch = getVaultStaffList(response).find((staff) => {
    const label = String(
      pickApiRowValue(staff, "UserName", "StaffName") ||
        [staff?.FirstName, staff?.LastName].filter(Boolean).join(" ") ||
        "",
    ).trim();
    return label && label.toLowerCase() === ownerName.toLowerCase();
  });

  if (!staffMatch?.StaffId) return null;
  return {
    StaffId: staffMatch.StaffId,
    UserName: ownerName,
  };
}

function getVaultDocumentPublisherMention(response) {
  const logs = getApiTable(response, "DocumentLog");
  const publishEntry = (logs || []).find((row) => {
    const action = String(row.ActionName || row.actionName || "").trim().toLowerCase();
    return action === "document published";
  });
  if (!publishEntry) return null;

  const publisherName = String(
    pickApiRowValue(publishEntry, "ActionBy", "actionBy") || "",
  ).trim();
  const publisherId = pickApiRowValue(
    publishEntry,
    "CreatedById",
    "CreatedBy",
    "createdById",
  );

  if (publisherId && publisherName) {
    return { StaffId: publisherId, UserName: publisherName };
  }

  if (!publisherName) return null;

  const staffMatch = getVaultStaffList(response).find((staff) => {
    const label = String(
      pickApiRowValue(staff, "UserName", "StaffName") ||
        [staff?.FirstName, staff?.LastName].filter(Boolean).join(" ") ||
        "",
    ).trim();
    return label && label.toLowerCase() === publisherName.toLowerCase();
  });

  if (!staffMatch?.StaffId) return null;
  return {
    StaffId: staffMatch.StaffId,
    UserName: publisherName,
  };
}

/** Owner, reviewers, approvers, and whoever published the document. */
export function getVaultCommentMentionCandidates(response) {
  const rows = [];

  getVaultSavedReviewers(response).forEach((row) => {
    appendVaultMentionStaff(rows, row.ReviewerId, row.ReviewerName);
  });

  getVaultSavedApprovers(response).forEach((row) => {
    appendVaultMentionStaff(rows, row.ApproverId, row.ApproverName);
  });

  const owner = getVaultDocumentOwnerMention(response);
  if (owner) rows.push(owner);

  const publisher = getVaultDocumentPublisherMention(response);
  if (publisher) rows.push(publisher);

  return dedupeVaultMentionStaff(rows);
}

export function getVaultWorkflowActions(response) {
  const info = getVaultDocumentInfo(response);
  const checkout = getVaultCheckoutRecords(response)[0] || {};
  const stage = String(info.DocumentStage || "");
  const isCheckedOut =
    Number(checkout.CheckOutStatus ?? info.IsCheckOut) === 1;
  const isPublished = stage === "Published";

  return {
    stage,
    isCheckedOut,
    isPublished,
    canSaveReviewers: !isCheckedOut && !isPublished,
    canSendReviewers: !isCheckedOut && !isPublished,
    canCompleteReview: !isCheckedOut && stage === "In Review",
    canSaveApprovers: !isCheckedOut && !isPublished,
    canSendApprovers:
      !isCheckedOut &&
      (stage === "Review Completed" || stage === "In Approval"),
    canCompleteApproval: !isCheckedOut && stage === "In Approval",
    canManageAssociatedDocs: !isCheckedOut,
    canManageQuiz: !isCheckedOut && !isPublished,
  };
}

export function canRemoveVaultReviewer(row) {
  const completedAt = String(row?.ReveiwCompletedDateTime || "").trim();
  const status = String(row?.ReviewStatus || "");
  return !completedAt && status !== "Completed";
}

export function canRemoveVaultApprover(row) {
  const completedAt = String(row?.ApprovalCompletedDateTime || "").trim();
  const status = String(row?.ApproveStatus || "");
  return (
    !completedAt && status !== "Approved" && status !== "Rejected"
  );
}

export function getAssignedVaultPeopleIds(savedRows, idField, completedField) {
  return new Set(
    (savedRows || [])
      .filter((row) => !String(row?.[completedField] || "").trim())
      .map((row) => String(row?.[idField] || ""))
      .filter(Boolean),
  );
}

export function getVaultQuizSettingsRow(questions) {
  const rows = Array.isArray(questions) ? questions : [];
  return (
    rows.find((row) => String(row?.QuizName || row?.QuizzName || "").trim()) ||
    rows.find((row) => String(row?.QuizId || row?.QuizzId || "").trim()) ||
    rows[0] ||
    {}
  );
}

export function buildVaultQuizViewModel(response) {
  const questions = getVaultQuizQuestions(response);
  const options = getVaultQuizOptions(response);
  const settings = getVaultQuizSettingsRow(questions);
  const linkedQuizId = String(settings.QuizId || settings.QuizzId || "").trim();
  const linkedQuizName =
    String(settings.QuizName || settings.QuizzName || "").trim() || "Linked quiz";
  const createdByName = String(
    pickApiRowValue(settings, "CreatedByName", "createdByName") || "",
  ).trim();
  const createdAt = pickApiRowValue(settings, "CreatedAt", "createdAt", "SystemDateTime");
  const documentBindingCount = Number(
    pickApiRowValue(settings, "DocumentBindingCount", "documentBindingCount") ?? 0,
  ) || 0;

  return {
    linkedQuiz: linkedQuizId
      ? {
          quizId: linkedQuizId,
          quizName: linkedQuizName,
          mappedQuestionCount: Number(settings.MappedQuestionCount ?? questions.length) || 0,
          questionsToServeCount: Number(settings.QuestionsToServeCount ?? 0) || 0,
          documentBindingCount,
          createdByName: createdByName || undefined,
          createdAt: createdAt ?? undefined,
        }
      : null,
    settings: {
      passingPercentage: settings.PassingPercentage ?? "",
      attemptLimit: settings.AttemptLimit ?? "",
      quizDueDate: settings.QuizDueDate ?? "",
      reminderFrequency: settings.ReminderFrequency ?? "Day",
      reminderInterval: settings.ReminderInterval ?? "",
      hasQuestions: questions.some((q) => String(q?.QuestionId || "").trim()),
    },
    questions: questions
      .filter((question) => String(question?.QuestionId || "").trim())
      .map((question, index) => ({
      ...question,
      index: index + 1,
      options: options.filter(
        (option) =>
          String(option.QuestionId) === String(question.QuestionId),
      ),
    })),
  };
}

export function buildVaultCommentThreads(comments) {
  const rows = Array.isArray(comments) ? comments : [];
  const byId = new Map(rows.map((row) => [String(row.CommentId), row]));
  const childrenByParent = new Map();

  rows.forEach((row) => {
    const parentId = row.PreviousCommentId;
    if (parentId == null || parentId === "") return;
    const key = String(parentId);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(row);
  });

  const roots = rows.filter(
    (row) => row.PreviousCommentId == null || row.PreviousCommentId === "",
  );

  return roots.map((root) => ({
    ...root,
    replies: (childrenByParent.get(String(root.CommentId)) || []).sort((a, b) =>
      String(a.SystemDateTime || "").localeCompare(String(b.SystemDateTime || "")),
    ),
  }));
}

export function getVaultVersionLabel(row) {
  return getVaultDocumentListId(row);
}

export function isVaultHistoricalPreview(previewRow, currentRow) {
  if (!previewRow || !currentRow || isAssociatedDocPreview(previewRow)) return false;
  const previewId = String(previewRow.DocumentId || "").trim().toLowerCase();
  const currentId = String(currentRow.DocumentId || "").trim().toLowerCase();
  if (previewId && currentId) return previewId !== currentId;
  return String(previewRow.VersionNumber ?? "") !== String(currentRow.VersionNumber ?? "");
}

export function isAssociatedDocPreview(previewRow) {
  return previewRow?.__previewKind === "associated";
}

export function buildAssociatedDocPreviewRow(doc) {
  const title = String(doc?.DocumentTitle || "Document").trim() || "Document";
  const extension = String(doc?.DocumentExtension || "").trim();
  const parsedExtension =
    extension ||
    (title.includes(".") ? title.slice(title.lastIndexOf(".") + 1) : "");

  return {
    DocumentId: doc?.DocumentIdRef || doc?.DocumentId || "",
    DocumentURL: doc?.DocumentURL || "",
    DocumentTitle: title,
    DocumentExtension: parsedExtension,
    VersionNumber: doc?.VersionNumber,
    __previewKind: "associated",
  };
}

export function getVaultAssociatedDocLabel(row) {
  const title = getVaultDocumentTitle(row);
  const typeAbbr = row?.DocumentTypeAbberiviation || "";
  const catAbbr = row?.DocumentCategoryAbberiviation || "";
  const uniqueNum = row?.DocumentCategoryUniqueNumber ?? "";
  if (typeAbbr || catAbbr || uniqueNum) {
    return `${typeAbbr}_${catAbbr}_${uniqueNum} — ${title}`;
  }
  return title;
}

export function formatVaultTableDate(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

/**
 * True after Send for review / Send for approval.
 * Workflow status is the source of truth ("Not Started", "Completed", etc.).
 * ReviewSentDate alone is NOT enough — SaveReviewer historically stamped a date
 * without setting status, which incorrectly disabled Send.
 */
export function isVaultPersonSent(row, statusField) {
  const status = String(row?.[statusField] ?? "").trim();
  return Boolean(status && status !== "—");
}

export function getVaultPersonSentDate(row, sentDateField, statusField) {
  const sentDate = formatVaultTableDate(row?.[sentDateField]);
  if (sentDate && sentDate !== "—") return sentDate;

  // Current-cycle assigned rows may only have AssignedDateTime until Send runs.
  const assignedDate = formatVaultTableDate(
    row?.AssignedDateTime ?? row?.assignedDateTime ?? row?.SystemDateTime,
  );
  if (assignedDate && assignedDate !== "—") return assignedDate;

  if (!isVaultPersonSent(row, statusField, sentDateField)) return "—";
  return sentDate;
}

function normalizePersonStatus(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function isPendingPersonStatus(value) {
  const normalized = normalizePersonStatus(value);
  return (
    !normalized
    || normalized === "—"
    || normalized === "assigned"
    || normalized === "sent"
    || normalized === "not started"
    || normalized === "notstarted"
  );
}

export function getVaultPersonDisplayStatus(row, statusField, sentDateField) {
  if (!isVaultPersonSent(row, statusField, sentDateField)) return "Assigned";

  const rawStatus = formatVaultTableDate(row[statusField]);
  const normalized = normalizePersonStatus(rawStatus);

  if (normalized === "not started" || normalized === "notstarted") {
    return "Sent";
  }

  if (statusField === "ReviewStatus" && (normalized === "inprogress" || normalized === "in progress")) {
    return "Reviewed";
  }

  return rawStatus;
}

function getVaultPersonActionDate(row, config) {
  return formatVaultTableDate(row?.[config.actionDateField]);
}

function getVaultPersonCompletedDate(row, config) {
  return formatVaultTableDate(row?.[config.completedAtField]);
}

/**
 * This person submitted from Shared With Me.
 * Owner cycle-close stamps IsReviewComplete / ReveiwCompletedDateTime on everyone —
 * those flags are not a personal action.
 */
export function hasVaultPersonActed(row, config) {
  if (!row || !config) return false;

  const actionDate = getVaultPersonActionDate(row, config);
  if (actionDate && actionDate !== "—") return true;

  const status = normalizePersonStatus(row[config.statusField]);
  if (isPendingPersonStatus(status)) return false;

  return (
    status.includes("complete")
    || status.includes("reviewed")
    || status.includes("inprogress")
    || status.includes("in progress")
    || status.includes("approved")
    || status.includes("reject")
  );
}

/** This person marked their own review/approval complete (not the owner cycle close). */
export function hasVaultPersonSelfCompleted(row, config) {
  if (!row || !config) return false;
  const status = normalizePersonStatus(row[config.statusField]);
  if (status.includes("complete") || status === "2") return true;
  if (config.statusField === "ApproveStatus" && (status.includes("approved") || status === "3")) {
    return true;
  }
  return false;
}

/**
 * Flag line on the person card.
 * "In review" / "In approval" until that person has acted.
 * Owner cycle-complete time must not appear as this person's completed date.
 */
export function getVaultPersonProgressLabel(row, config) {
  const pendingLabel = config.pendingCompletedLabel || config.completedLabel;
  const actionDate = getVaultPersonActionDate(row, config);
  const completedAt = getVaultPersonCompletedDate(row, config);
  const status = normalizePersonStatus(row?.[config.statusField]);
  const actionedLabel = config.actionedLabel || "Reviewed";
  const selfCompletedDate =
    completedAt && completedAt !== "—" ? completedAt : actionDate;

  if (hasVaultPersonSelfCompleted(row, config)) {
    return selfCompletedDate && selfCompletedDate !== "—"
      ? `${config.completedLabel}: ${selfCompletedDate}`
      : config.completedLabel;
  }

  if (status.includes("reject")) {
    return actionDate && actionDate !== "—" ? `Rejected: ${actionDate}` : "Rejected";
  }

  if (hasVaultPersonActed(row, config)) {
    return actionDate && actionDate !== "—" ? `${actionedLabel}: ${actionDate}` : actionedLabel;
  }

  return pendingLabel;
}

export function getUnsentVaultPeopleIds(savedRows, idField, statusField, sentDateField) {
  return (savedRows || [])
    .filter((row) => !isVaultPersonSent(row, statusField, sentDateField))
    .map((row) => String(row?.[idField] || ""))
    .filter(Boolean);
}

export function getVaultPeopleWorkflowHint(mode, workflow, savedRows, statusField) {
  const stage = String(workflow?.stage || "").trim();
  const unsentCount = getUnsentVaultPeopleIds(
    savedRows,
    mode === "reviewers" ? "ReviewerId" : "ApproverId",
    statusField,
    mode === "reviewers" ? "ReviewSentDate" : "ApproveSentDate",
  ).length;
  const sentCount = savedRows.length - unsentCount;

  if (workflow?.isCheckedOut) {
    return "Document is checked out. Reviewer and approver actions are disabled.";
  }
  if (workflow?.isPublished) {
    return "Document is published. Workflow actions are no longer available here.";
  }

  if (mode === "reviewers") {
    if (stage === "In Review") {
      return "Document is in review. Use Review complete when you decide the review cycle is finished.";
    }
    if (stage === "Review Completed") {
      return "Review cycle is complete. Continue with approvers when ready.";
    }
    if (unsentCount > 0) {
      return `${unsentCount} reviewer(s) saved but not sent. Click Send for review to move the document to In Review.`;
    }
    if (sentCount > 0 && stage !== "In Review") {
      return "Reviewers were sent. The document should be in review.";
    }
    return "Save reviewers first, then send for review when you are ready to start the review cycle.";
  }

  if (stage === "In Approval") {
    return "Document is in approval. Use Approval complete when you decide the approval cycle is finished.";
  }
  if (stage === "Approval Completed") {
    return "Approval cycle is complete.";
  }
  if (unsentCount > 0) {
    return `${unsentCount} approver(s) saved but not sent. Click Send for approval when ready.`;
  }
  return "Save approvers first, then send for approval after review is completed.";
}
